import React, { useState, useEffect } from "react";
import { StyleSheet, View, TextInput, Linking, Platform, type StyleProp, type ViewStyle } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../../ThemedText";
import { ThemedButton } from "../../ThemedButton";
import { ThemedToolTip } from "../../ThemedToolTip";
import { useThemeColor } from "@/hooks/useThemeColor";

export interface IWebLinkToolbarProps {
  visible?: boolean;
  position?: { top: number; left: number };
  initialUrl?: string;
  initialTitle?: string;
  isExistingLink?: boolean;
  onApply?: (url: string, title: string) => void;
  onSave?: (url: string, title: string) => void;
  onUnlink?: () => void;
  onClose?: () => void;
  isStandalonePreview?: boolean;
  style?: StyleProp<ViewStyle>;
}

/**
 * WebLinkToolbar (formerly LinkFormatToolbar)
 *
 * Compact floating popup toolbar for web link insertion & editing.
 * Features:
 * - Half the original height with horizontal single-row inputs.
 * - Standardized ThemedButton components for all actions (Apply, Visit, Unlink, Close).
 * - Prevents mousedown default on web to preserve active selection.
 * - Strictly respects Emerald/Blue/Rose palette (Strict Color Ban).
 */
export const WebLinkToolbar: React.FC<IWebLinkToolbarProps> = ({
  visible = true,
  position,
  initialUrl = "",
  initialTitle = "",
  isExistingLink,
  onApply,
  onSave,
  onUnlink,
  onClose,
  isStandalonePreview = false,
  style,
}) => {
  const [url, setUrl] = useState(initialUrl);
  const [title, setTitle] = useState(initialTitle);

  useEffect(() => {
    setUrl(initialUrl);
  }, [initialUrl]);

  useEffect(() => {
    setTitle(initialTitle);
  }, [initialTitle]);

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textColor = useThemeColor({}, "text");
  const textMuted = useThemeColor({}, "textMuted");

  if (!visible) return null;

  const handleApply = () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return;
    const saveHandler = onSave || onApply;
    saveHandler?.(trimmedUrl, title.trim());
  };

  const handleOpenLink = () => {
    if (!url.trim()) return;
    try {
      const targetUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      Linking.openURL(targetUrl);
    } catch (err) {
      console.warn("Could not open link:", err);
    }
  };

  const containerStyle = isStandalonePreview
    ? [
        styles.popupContainer,
        {
          backgroundColor: cardBg,
          borderColor,
          position: "relative" as const,
          top: 0,
          left: 0,
        },
        style,
      ]
    : [
        styles.popupContainer,
        {
          backgroundColor: cardBg,
          borderColor,
          position: "absolute" as const,
          zIndex: 1000,
          top: position ? Math.max(8, position.top) : 8,
          left: position ? Math.max(8, position.left) : 16,
        },
        style,
      ];

  const hasExistingLink = isExistingLink !== undefined ? isExistingLink : Boolean(initialUrl);

  return (
    <View
      style={containerStyle}
      testID="web-link-toolbar"
      {...(Platform.OS === "web"
        ? ({
            onMouseDown: (e: any) => e.preventDefault(),
          } as any)
        : {})}
    >
      <View style={styles.compactRow}>
        <Ionicons name="link-outline" size={14} color={textColor} style={{ marginRight: 2 }} />

        {/* URL Input */}
        <View style={styles.inputFieldWrapper}>
          <TextInput
            value={url}
            onChangeText={setUrl}
            placeholder="URL (https://...)"
            placeholderTextColor={textMuted}
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="url"
            style={[styles.compactInput, { color: textColor, borderColor }]}
            onSubmitEditing={handleApply}
          />
        </View>

        {/* Title Input */}
        <View style={styles.inputFieldWrapper}>
          <TextInput
            value={title}
            onChangeText={setTitle}
            placeholder="Label (optional)"
            placeholderTextColor={textMuted}
            style={[styles.compactInput, { color: textColor, borderColor }]}
            onSubmitEditing={handleApply}
          />
        </View>

        {/* Apply Action Button */}
        <ThemedButton
          title="Apply"
          variant="primary"
          style={styles.actionBtn}
          textStyle={styles.btnText}
          onPress={handleApply}
          accessibilityLabel="Apply Link"
        />

        {/* Visit Existing Link */}
        {hasExistingLink && (
          <ThemedToolTip text="Visit URL">
            <ThemedButton
              title=""
              icon={<Ionicons name="open-outline" size={13} color={textColor} />}
              variant="minimal"
              style={styles.actionBtn}
              onPress={handleOpenLink}
              accessibilityLabel="Visit link"
            />
          </ThemedToolTip>
        )}

        {/* Unlink Action */}
        {hasExistingLink && onUnlink && (
          <ThemedToolTip text="Remove link">
            <ThemedButton
              title=""
              icon={<Ionicons name="trash-outline" size={13} color="#EF4444" />}
              variant="minimal"
              style={[styles.actionBtn, { borderColor: "rgba(239, 68, 68, 0.3)" }]}
              onPress={onUnlink}
              accessibilityLabel="Remove link"
            />
          </ThemedToolTip>
        )}

        {/* Close Button */}
        {onClose && (
          <ThemedButton
            title=""
            icon={<Ionicons name="close" size={13} color={textColor} />}
            variant="minimal"
            style={styles.actionBtn}
            onPress={onClose}
            accessibilityLabel="Close link popup"
          />
        )}
      </View>
    </View>
  );
};

// Aliased export for backwards compatibility
export const LinkFormatToolbar = WebLinkToolbar;

const styles = StyleSheet.create({
  popupContainer: {
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 5,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 10,
    elevation: 8,
    alignSelf: "flex-start",
    maxWidth: "96%",
  },
  compactRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  inputFieldWrapper: {
    minWidth: 100,
    flexShrink: 1,
  },
  compactInput: {
    height: 26,
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 6,
    fontSize: 11,
    paddingVertical: 2,
  },
  actionBtn: {
    height: 26,
    minWidth: 26,
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 4,
  },
  btnText: {
    fontSize: 11,
    fontWeight: "700",
  },
});
